import { z } from 'zod';
import type { CollageDocument } from './document';

const identifier = z.string().min(1).max(128).regex(/^[A-Za-z0-9_-]+$/);
const color = z.string().regex(/^#[\da-fA-F]{6}(?:[\da-fA-F]{2})?$/);
const positive = z.number().finite().positive();
const nonNegative = z.number().finite().nonnegative();
const hash = z.string().regex(/^[\da-f]{64}$/);
const internalPath = (prefix: string, extension: string): z.ZodString =>
  z.string().regex(new RegExp(`^${prefix}/[A-Za-z0-9_-]{1,128}\\.${extension}$`));

const transformSchema = z.object({
  cx: z.number().finite(),
  cy: z.number().finite(),
  width: positive,
  height: positive,
  rotation: z.number().finite().transform((value) => ((value % 360) + 360) % 360),
});

const baseElementSchema = z.object({
  id: identifier,
  name: z.string().min(1).max(256),
  transform: transformSchema,
  visible: z.boolean(),
  locked: z.boolean(),
  opacity: z.number().finite().min(0).max(1),
  shadowColor: color.default('#000000'),
  shadowBlur: z.number().finite().min(0).max(100).default(0),
  shadowOffsetX: z.number().finite().min(-500).max(500).default(0),
  shadowOffsetY: z.number().finite().min(-500).max(500).default(0),
});

const photoSchema = baseElementSchema.extend({
  type: z.literal('photo'),
  assetId: identifier.nullable(),
  slotId: identifier.nullable(),
  fit: z.enum(['cover', 'contain']),
  imageOpacity: z.number().finite().min(0).max(1).default(1),
  imageBlur: z.number().finite().min(0).max(100).default(0),
  imageBrightness: z.number().finite().min(0).max(2).default(1),
  imageSaturation: z.number().finite().min(0).max(2).default(1),
  crop: z.object({
    zoom: z.number().finite().min(1).max(10),
    offsetX: z.number().finite(),
    offsetY: z.number().finite(),
    quarterTurns: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
    flipX: z.boolean(),
    flipY: z.boolean(),
  }),
  frame: z.object({
    fill: color,
    radius: nonNegative,
    borderWidth: nonNegative,
    borderColor: color,
  }),
}).superRefine((photo, context) => {
  if (photo.fit === 'contain' && photo.crop.zoom !== 1) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['crop', 'zoom'], message: 'Contain photos must use zoom 1' });
  }
  if (photo.fit === 'contain' && (photo.crop.offsetX !== 0 || photo.crop.offsetY !== 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['crop'], message: 'Contain photos cannot have crop offsets' });
  }
});

const textSchema = baseElementSchema.extend({
  type: z.literal('text'),
  text: z.string().max(10000),
  fontId: identifier,
  fontSize: positive,
  fontWeight: z.number().int().min(100).max(900),
  italic: z.boolean(),
  fill: color,
  align: z.enum(['left', 'center', 'right']),
  lineHeight: positive,
  letterSpacing: z.number().finite(),
  curve: z.number().finite().min(-4000).max(4000).default(0),
  slant: z.number().finite().min(-1.5).max(1.5).default(0),
  depth: z.number().finite().min(0).max(100).default(0),
  depthColor: color.default('#000000'),
});

export const BUILTIN_FONT_FAMILIES: Record<string, string> = {
  'builtin-sans': 'Arial',
  'builtin-serif': 'Georgia',
  'builtin-mono': 'monospace',
};

const assetSchema = z.object({
  id: identifier,
  path: z.string().min(1).max(256),
  originalName: z.string().min(1).max(1024),
  mime: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']),
  width: positive,
  height: positive,
  sha256: hash,
}).superRefine((asset, context) => {
  const extension = asset.mime === 'image/jpeg' ? 'jpe?g' : asset.mime === 'image/png' ? 'png' : asset.mime === 'image/svg+xml' ? 'svg' : 'webp';
  const expectedPath = internalPath('assets', extension).safeParse(asset.path).success;
  if (!expectedPath) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['path'], message: 'Asset path must use a safe internal filename matching its image type' });
  }
  if (asset.width * asset.height > 50_000_000) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['width'], message: 'Image exceeds the 50 megapixel limit' });
  }
});

const fontSchema = z.object({
  id: identifier,
  path: internalPath('fonts', 'ttf|otf|woff2'),
  family: z.string().min(1).max(256),
  weight: z.number().int().min(100).max(900),
  style: z.enum(['normal', 'italic']),
  sha256: hash,
  licensePath: internalPath('licenses', 'txt').optional(),
});

const slotSchema = z.object({
  id: identifier,
  x: z.number().finite().nonnegative(),
  y: z.number().finite().nonnegative(),
  width: positive,
  height: positive,
});

export const collageDocumentSchema = z.object({
  schemaVersion: z.literal(1),
  id: identifier,
  name: z.string().min(1).max(256),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  canvas: z.object({
    width: z.number().finite().int().min(64).max(8192),
    height: z.number().finite().int().min(64).max(8192),
    ppi: positive,
    blackAndWhite: z.boolean().default(false),
    background: color.nullable(),
    backgroundAssetId: identifier.nullable().default(null),
    backgroundImageOpacity: z.number().finite().min(0).max(1).default(1),
    backgroundImageBlur: z.number().finite().min(0).max(100).default(0),
    backgroundImageBrightness: z.number().finite().min(0).max(2).default(1),
    backgroundImageSaturation: z.number().finite().min(0).max(2).default(1),
    backgroundImageOffsetX: z.number().finite().min(-100).max(100).default(0),
    backgroundImageOffsetY: z.number().finite().min(-100).max(100).default(0),
  }).superRefine((canvas, context) => {
    if (canvas.width * canvas.height > 32_000_000) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['width'], message: 'Canvas exceeds the 32 megapixel limit' });
    }
  }),
  assets: z.record(identifier, assetSchema).superRefine((assets, context) => {
    const entries = Object.entries(assets);
    if (entries.length > 100) context.addIssue({ code: z.ZodIssueCode.custom, message: 'A project can contain at most 100 images' });
    for (const [key, asset] of entries) {
      if (key !== asset.id) context.addIssue({ code: z.ZodIssueCode.custom, path: [key, 'id'], message: 'Asset key must match its id' });
    }
  }),
  fonts: z.record(identifier, fontSchema).superRefine((fonts, context) => {
    const entries = Object.entries(fonts);
    if (entries.length > 50) context.addIssue({ code: z.ZodIssueCode.custom, message: 'A project can contain at most 50 fonts' });
    for (const [key, font] of entries) {
      if (key !== font.id) context.addIssue({ code: z.ZodIssueCode.custom, path: [key, 'id'], message: 'Font key must match its id' });
    }
  }),
  elements: z.array(z.union([photoSchema, textSchema])).max(200),
  layout: z.object({
    templateId: identifier,
    templateVersion: z.number().int().positive(),
    margin: nonNegative,
    gap: nonNegative,
    slots: z.array(slotSchema).max(200),
  }).nullable(),
}).superRefine((document, context) => {
  if (document.canvas.backgroundAssetId !== null && !document.assets[document.canvas.backgroundAssetId]) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['canvas', 'backgroundAssetId'], message: 'Canvas background references a missing asset' });
  }
  const elementIds = new Set<string>();
  const occupiedSlots = new Set<string>();
  const slots = new Set(document.layout?.slots.map((slot) => slot.id) ?? []);

  if (document.layout && slots.size !== document.layout.slots.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['layout', 'slots'], message: 'Slot ids must be unique' });
  }
  for (const element of document.elements) {
    if (elementIds.has(element.id)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['elements'], message: `Duplicate element id: ${element.id}` });
    }
    elementIds.add(element.id);

    if (element.type === 'photo') {
      if (element.assetId !== null && !document.assets[element.assetId]) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['elements'], message: `Photo ${element.id} references missing asset ${element.assetId}` });
      }
      if (element.slotId !== null) {
        if (!document.layout || !slots.has(element.slotId)) {
          context.addIssue({ code: z.ZodIssueCode.custom, path: ['elements'], message: `Photo ${element.id} references missing layout slot ${element.slotId}` });
        }
        if (occupiedSlots.has(element.slotId)) {
          context.addIssue({ code: z.ZodIssueCode.custom, path: ['elements'], message: `Layout slot ${element.slotId} is used more than once` });
        }
        occupiedSlots.add(element.slotId);
      }
    } else if (!document.fonts[element.fontId] && !BUILTIN_FONT_FAMILIES[element.fontId] && !/^system-[A-Za-z0-9_-]+$/.test(element.fontId)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['elements'], message: `Text ${element.id} references missing font ${element.fontId}` });
    }
  }
});

export type ValidatedCollageDocument = z.infer<typeof collageDocumentSchema>;

export function validateDocument(input: unknown): CollageDocument {
  return collageDocumentSchema.parse(input) as CollageDocument;
}
